import {
	Autocomplete,
	Box,
	Button,
	Modal,
	OutlinedInput,
	TextField,
} from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import styled from 'styled-components';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useDispatch } from 'react-redux';
import {
	generateScratchCards,
	getScratchCards,
} from '@Features/scratchCards/scratchCardsSlice';
import {
	fetchBatchesDropdown,
	fetchClassroomsByInstructorDropdown,
	fetchCoursesDropdown,
	fetchStandaloneCoursesDropdown,
	fetchBundlesDropdown,
} from '@Features/dropdowns/dropdownsSlice';
import { Autocomplete as EdAutocomplete } from '@Components/Autocomplete/Autocomplete';
import usePlatformMode from '@Hooks/usePlatformMode';
import { productOptionLabel } from '@Features/dropdowns/dropdownsSlice.types';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import EdNumericInput from '@Components/UI/Inputs/EdNumericInput/EdNumericInput';
import { Divider, Spacer } from '@Styled/utilities';
import {
	promoCodesCreate,
	promoCodesGenerate,
	promoCodesUpdate,
} from '../Slices/PromoCodes.slice';
import useFilters from '@Hooks/useFilters';
import useQueryURL from '@Hooks/useQueryUrl';
import { Switch } from '@mui/material';
import { DevTool } from '@hookform/devtools';
import { FlexLayout, GridLayout } from '@eduact/ed-system';
import { CreatePromoCode, PromoCode } from '../Types/PromoCodes.types';
import EdFormControl, {
	TextFieldLabel,
} from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import EdBox from '@Components/UI/Utilities/EdBox/EdBox';
import { EdFormControlLabel } from '@Components/UI/Inputs/EdFormControlLabel/EdFormControlLabel';
import { DatePicker } from '@mui/x-date-pickers';
import { useRouteProvider } from '@Providers/Routes/RoutesProvider';
import { debounce, differenceBy, intersection, omit, xor, xorBy, xorWith } from 'lodash';
import { useSnackbar } from '@Providers/useSnackbar';
import { searchStudentsRequest } from '@Services/dropdowns/dropdowns.req';
import SelectChip from '@Components/UI/Inputs/SelectChip/SelectChip';
import api from '@Services/api';

type Props = {
	data?: { id: number } & PromoCode;
};

export type CourseFormData = {
	classroom_id: string;
	course_id: number | null;
	standalone_course_id: number | null;
	quantity: number;
	scheme: string;
	instructor_id: number | null;
	value: number;
};

const SCHEMA = yup.object({
	id: yup.number().nullable(true),
	name: yup.string().optional(),
	code: yup.string().when('id', {
		is: (id: any) => id !== undefined,
		then: (s) => s.nullable(true).max(10),
		otherwise: (s) => s.required().max(10),
	}),
	description: yup.string().nullable(true),
	max_uses: yup.number().nullable(true),
	max_uses_per_user: yup.number().nullable(true),
	value: yup.number().required(),
	value_type: yup.mixed().oneOf(['percentage', 'flat']).required(),
	value_cap: yup
		.number()
		.nullable(true)
		.transform((v) => {
			return isNaN(v) ? null : v;
		}),
	min_amount_to_apply: yup.number().nullable(true),
	expires_at: yup
		.date()
		.nullable(true)
		.transform((value) => (value === undefined ? null : value)),
	user_restriction: yup.boolean().optional(),
	instructor_id: yup
		.number()
		.nullable(true)
		.transform((value) => (isNaN(value) ? null : value)),
	classroom_id: yup
		.number()
		.nullable(true)
		.transform((value) => (isNaN(value) ? null : value)),
	course_id: yup
		.number()
		.nullable(true)
		.transform((value) => (isNaN(value) ? null : value)),
	standalone_course_id: yup.number().nullable(true).transform((value) => (isNaN(value) ? null : value)),
	bundle_id: yup.number().nullable(true).transform((value) => (isNaN(value) ? null : value)),
	restricted_to_new_users: yup.boolean().nullable(true),
	users: yup.array(),
});
const GenerateCodes = ({ data }: Props) => {
	const dispatch = useAppThunkDispatch();
	const {
		formState: { isValid, errors, isDirty },
		getValues,
		watch,
		reset,
		control,
		handleSubmit,
		setValue,
	} = useForm<CreatePromoCode>({
		resolver: yupResolver(SCHEMA),
		mode: 'onTouched',
		defaultValues: {
			...omit(data, 'code', 'users'),
			users: data?.users ?? [],
		} ?? { expires_at: null },
	});

	const {
		dropdownInstructors,
		dropdownClassrooms,
		dropdownCourses,
		dropdownClassroomsByInstructor,
		dropdownStandaloneCourses,
		dropdownBundles,
	} = useTypedSelector((state) => state.dropdowns);
	const { classroomsEnabled, standaloneEnabled, bundlesEnabled } =
		usePlatformMode();

	const classroom_id = watch('classroom_id');
	const instructor_id = watch('instructor_id');

	// Courses load per classroom, like the scratch-cards modal. The previous
	// memo read the embedded courses of the unrelated dropdownClassrooms list,
	// which nothing populates here — so the Course options never mounted.
	useEffect(() => {
		if (classroom_id) dispatch(fetchCoursesDropdown(classroom_id));
	}, [classroom_id, dispatch]);

	// Picking a different instructor invalidates the chosen classroom and
	// course; a different classroom invalidates the course. The refs skip the
	// mount pass so an edited promocode keeps its prefilled targets.
	const prevInstructorRef = useRef(instructor_id);
	useEffect(() => {
		if (prevInstructorRef.current !== instructor_id) {
			setValue('classroom_id', null as any);
			setValue('course_id', null as any);
		}
		prevInstructorRef.current = instructor_id;
	}, [instructor_id, setValue]);
	const prevClassroomRef = useRef(classroom_id);
	useEffect(() => {
		if (prevClassroomRef.current !== classroom_id) {
			setValue('course_id', null as any);
		}
		prevClassroomRef.current = classroom_id;
	}, [classroom_id, setValue]);

	// The Autocomplete wrapper reports the picked id through target.value.
	const pickId = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value as unknown as string | number | null;
		return raw === null || raw === '' ? null : Number(raw);
	};

	const classroomTarget = watch('classroom_id');
	const courseTarget = watch('course_id');
	const standaloneTarget = watch('standalone_course_id');
	const bundleTarget = watch('bundle_id');

	useEffect(() => {
		if (standaloneEnabled) dispatch(fetchStandaloneCoursesDropdown(undefined));
		if (bundlesEnabled) dispatch(fetchBundlesDropdown(undefined));
	}, [standaloneEnabled, bundlesEnabled, dispatch]);

	// A promocode targets exactly one product. Standalone courses and bundles are
	// top-level products, so picking one clears everything else — this mirrors the
	// `promocodes_specific_target_check` constraint, which the backend now also
	// enforces so a direct API call cannot bypass it.
	useEffect(() => {
		if (standaloneTarget) {
			setValue('classroom_id', null as any);
			setValue('course_id', null as any);
			setValue('bundle_id', null as any);
		}
	}, [standaloneTarget, setValue]);
	useEffect(() => {
		if (bundleTarget) {
			setValue('classroom_id', null as any);
			setValue('course_id', null as any);
			setValue('standalone_course_id', null as any);
		}
	}, [bundleTarget, setValue]);
	useEffect(() => {
		if (classroomTarget || courseTarget) {
			setValue('standalone_course_id', null as any);
			setValue('bundle_id', null as any);
		}
	}, [classroomTarget, courseTarget, setValue]);

	useEffect(() => {
		if (!instructor_id) return;
		dispatch(fetchClassroomsByInstructorDropdown(instructor_id));
	}, [instructor_id]);

	const { displaySnackbar } = useSnackbar();
	const onSubmitForm = async (formData: CreatePromoCode) => {
		try {
			if (data) {
				const resp = await dispatch(
					promoCodesUpdate({
						...formData,
						users: formData.users.map((_) => _.id),
					} as any)
				).unwrap();
				displaySnackbar('success', resp.message);
			} else {
				const resp = await dispatch(
					promoCodesCreate({
						...formData,
						users: formData.users.map((_) => _.id),
					} as any)
				).unwrap();
				displaySnackbar('success', resp.message);
			}
			onClose();
		} catch (error) {
			displaySnackbar(
				'error',
				error?.response?.data?.message || 'Something went wrong'
			);
		}
	};

	const parent = useRouteProvider();
	const onClose = () => {
		parent?.goToParent();
	};

	const students = watch('users');

	const [openAutoComplete, setOpenAutoComplete] = useState(false);
	const [query, setQuery] = useState('');
	const handleAutoComplete = async (value: string) => {
		if (value.length < 3) {
			setOptions([]);
			return;
		}
		try {
			setLoading(true);
			const { data } = await searchStudentsRequest(value);
			const mapped = data.data.map((_) => ({
				id: _.id,
				username: _.username,
				label: `${[_.first_name, _.last_name].filter(Boolean).join(' ')} (${
					_.username
				})`,
			}));
			// differenceBy, not xorBy: the symmetric difference used to inject
			// already-picked students back into the dropdown.
			setOptions(differenceBy(mapped, getValues().users, (_) => _.id));
		} catch (error) {
			displaySnackbar('error', 'Student search failed');
			setOptions([]);
		} finally {
			setLoading(false);
		}
	};
	const debounceFn = useCallback(debounce(handleAutoComplete, 1000), []);

	const handleQuery = (value: string) => {
		setQuery(value);
		debounceFn(value);
	};
	const [options, setOptions] = useState<
		Array<{ id: number; username: string; label?: string }>
	>([]);

	// A real in-flight flag: the old open-and-empty derivation rendered
	// "Loading..." forever for empty results and failed requests alike.
	const [loading, setLoading] = useState(false);

	const value_type = watch('value_type');

	const user_restriction = watch('user_restriction');

	console.log({ user_restriction });

	return (
		<>
			 
			<Modal
				open={true}
				onClose={onClose}
				sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
			>
				<FlexLayout>
					<EdBox
						minWidth={'60vw'}
						minHeight={'60vh'}
						bgcolor={'white'}
						width="fit-content"
						maxWidth={'75vw'}
						height="fit-content"
					>
						<form onSubmit={handleSubmit(onSubmitForm, (e) => console.log(e))}>
							<FlexLayout mb="1rem">
								<TitleStyled id="modal-modal-title">Generate Codes</TitleStyled>
							</FlexLayout>
							<Divider label="Info" />
							<GridLayout
								gridGap={'1.5rem'}
								gridTemplateColumns={{
									sm: 'repeat(1,1fr)',
									md: 'repeat(2,1fr)',
									lg: 'repeat(3,1fr)',
								}}
							>
								<EdFormControl control={control} name="name" label={'Name'} />
								{!data && (
									<EdFormControl
										required
										inputProps={{ maxLength: 10 }}
										control={control}
										name="code"
										label={'Code'}
									/>
								)}
								<EdFormControl
									control={control}
									name="value_type"
									label={'Type'}
									required
									select
								>
									<MenuItem value="percentage">Percentage</MenuItem>
									<MenuItem value="flat">Flat</MenuItem>
								</EdFormControl>
								<Controller
									control={control}
									name="value"
									render={({ field }) => (
										<FlexLayout>
											<TextFieldLabel required>Value</TextFieldLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>
								{value_type === 'percentage' && (
									<Controller
										control={control}
										name="value_cap"
										render={({ field: { ref, ...field } }) => (
											// render={({ field }) => (
											<FlexLayout>
												<EdFormControlLabel>Value Cap</EdFormControlLabel>
												<EdNumericInput size="small" fullWidth {...field} />
											</FlexLayout>
										)}
									/>
								)}
								<EdFormControl
									control={control}
									name="description"
									label={'Description'}
									multiline
									minRows={2}
								/>
							</GridLayout>
							<Divider label="Usage" />
							<GridLayout
								gridGap={'1.5rem'}
								gridTemplateColumns={{
									sm: 'repeat(1,1fr)',
									md: 'repeat(2,1fr)',
									lg: 'repeat(3,1fr)',
								}}
							>
								<Controller
									control={control}
									name="max_uses"
									render={({ field: { ref, ...field } }) => (
										<FlexLayout>
											<EdFormControlLabel>Max usage</EdFormControlLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>
								<Controller
									control={control}
									name="max_uses_per_user"
									render={({ field: { ref, ...field } }) => (
										<FlexLayout>
											<EdFormControlLabel>
												Max usage per user
											</EdFormControlLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>
								<Controller
									control={control}
									name="min_amount_to_apply"
									render={({ field: { ref, ...field } }) => (
										// render={({ field }) => (
										<FlexLayout>
											<EdFormControlLabel>Min Value</EdFormControlLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>

								<Controller
									control={control}
									name="expires_at"
									render={({ field: { onChange, value, ...field } }) => {
										const _value = value ?? null;
										return (
											<FlexLayout>
												<EdFormControlLabel>Expires At</EdFormControlLabel>
												<DatePicker
													{...field}
													value={_value}
													onChange={(v) => onChange(v)}
													renderInput={(params) => (
														<TextField {...params} size="small" />
													)}
												/>
											</FlexLayout>
										);
									}}
								/>
							</GridLayout>
							<Divider label="Target" />
							<GridLayout
								gridGap={'1.5rem'}
								gridTemplateColumns={{
									sm: 'repeat(1,1fr)',
									md: 'repeat(2,1fr)',
									lg: 'repeat(3,1fr)',
								}}
							>
								<Controller
									control={control}
									name="instructor_id"
									render={({ field: { onChange, ...others }, fieldState }) => (
										<EdAutocomplete
											options={dropdownInstructors.map((inst) => ({
												id: inst.id,
												label: `${inst.first_name} ${inst.last_name}`,
											}))}
											{...others}
											fieldState={fieldState}
											label="Instructor"
											onChange={(e) =>
												setValue('instructor_id', pickId(e) as any, {
													shouldDirty: true,
													shouldValidate: true,
												})
											}
										/>
									)}
								/>
								{classroomsEnabled && (
									<Controller
										control={control}
										name="classroom_id"
										render={({ field: { onChange, ...others }, fieldState }) => (
											<EdAutocomplete
												options={dropdownClassroomsByInstructor.map((cl) => ({
													id: cl.id,
													label: `${cl.title} (${cl.label})`,
												}))}
												{...others}
												fieldState={fieldState}
												label="Classroom"
												disabled={!instructor_id}
												onChange={(e) =>
													setValue('classroom_id', pickId(e) as any, {
														shouldDirty: true,
														shouldValidate: true,
													})
												}
											/>
										)}
									/>
								)}
								{classroomsEnabled && (
									<Controller
										control={control}
										name="course_id"
										render={({ field: { onChange, ...others }, fieldState }) => (
											<EdAutocomplete
												options={dropdownCourses.map((course) => ({
													id: course.id,
													label: course.name,
												}))}
												{...others}
												fieldState={fieldState}
												label="Course"
												disabled={!classroom_id}
												onChange={(e) =>
													setValue('course_id', pickId(e) as any, {
														shouldDirty: true,
														shouldValidate: true,
													})
												}
											/>
										)}
									/>
								)}
								<EdFormControl
									control={control}
									name="user_restriction"
									label={'User restriction'}
									switch
								/>
								{standaloneEnabled && (
									<EdFormControl control={control} name="standalone_course_id" label="Standalone Course" select>
										<MenuItem selected value={''}>None</MenuItem>
										{dropdownStandaloneCourses.filter((course) => !instructor_id || Number(course.instructor_id) === Number(instructor_id)).map((course) => (
											<MenuItem key={course.id} value={course.id}>{productOptionLabel(course)}</MenuItem>
										))}
									</EdFormControl>
								)}
								{bundlesEnabled && (
									<EdFormControl control={control} name="bundle_id" label="Bundle" select>
										<MenuItem selected value={''}>None</MenuItem>
										{dropdownBundles.filter((bundle) => !instructor_id || Number(bundle.instructor_id) === Number(instructor_id)).map((bundle) => (
											<MenuItem key={bundle.id} value={bundle.id}>{productOptionLabel(bundle)}</MenuItem>
										))}
									</EdFormControl>
								)}

								{user_restriction && (
									<Controller
										control={control}
										name="users"
										render={({ field }) => (
											<FlexLayout flexDirection={'column'} width={'100%'}>
												<FlexLayout>
													<EdFormControlLabel>Students</EdFormControlLabel>
													<Autocomplete
														size="small"
														fullWidth
														multiple
														open={openAutoComplete}
														onOpen={() => setOpenAutoComplete(true)}
														onClose={() => setOpenAutoComplete(false)}
														options={options}
														// The server already filtered (username OR phone).
														// MUI's default filter would re-filter against the
														// label and hide every phone-number match.
														filterOptions={(results) => results}
														noOptionsText="No students found"
														getOptionLabel={(option: {
															id: number;
															username: string;
															label?: string;
														}) => option.label ?? option.username}
														loading={loading}
														onChange={(e, value) => {
															console.log(value);
															field.onChange(value);
														}}
														onInputChange={(e, v) => handleQuery(v)}
														inputValue={query}
														value={field.value}
														renderTags={() => <></>}
														renderInput={({ ...params }) => (
															<TextField {...params} />
														)}
													/>
												</FlexLayout>

												<FlexLayout flexWrap={'wrap'}>
													{students.map((i, index) => {
														return (
															<>
																<SelectChip
																	onDelete={() =>
																		// onDeleteValue('educationLanguages', i)
																		setValue(
																			'users',
																			xorBy(students, [i], (_) => _.id)
																		)
																	}
																	label={i.username}
																/>
															</>
														);
													})}
												</FlexLayout>
											</FlexLayout>
										)}
									/>
								)}
								<EdFormControl
									control={control}
									name="restricted_to_new_users"
									label={'New users restriction'}
									switch
								/>
							</GridLayout>
							<ButtonsContainer>
								<Button
									variant="text"
									color="inherit"
									sx={{
										fontFamily: 'Roboto',
										fontSize: '14px',
										padding: '6px 16px',
										color: '#6c63ff',
										backgroundColor: '#fff',
										outline: 'none',
									}}
									disabled={!isValid || !isDirty}
									type="submit"
								>
									Submit
								</Button>
								<Button
									variant="text"
									color="warning"
									sx={{
										fontFamily: 'Roboto',
										fontSize: '14px',
										padding: '6px 16px',
										color: '#ff9800',
										outline: 'none',
										backgroundColor: '#fff',
									}}
									disabled={!isDirty}
									onClick={() => {
										reset();
									}}
								>
									Cancel
								</Button>
							</ButtonsContainer>
						</form>
					</EdBox>
				</FlexLayout>
			</Modal>
		</>
	);
};

export default GenerateCodes;

export const Container = styled(Box)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	min-width: 23.625rem;
	min-height: 21rem;
	border-radius: 4px;
	border: 0;
	box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.38);
	margin: 39px 80px 50px 11.4px;
	padding: 12px 0 0;
	background-color: #ffffff;
	outline: none;
`;

export const TitleStyled = styled.span`
	font-weight: 500;
	font-family: Roboto;
	text-align: left;
	font-size: 1rem;
	color: rgba(0, 0, 0, 0.6);
`;

export const ButtonsContainer = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 0.5rem;
	outline: none;
	margin-top: 1.625rem;
`;

export const RadioGroupStyled = styled(RadioGroup)`
	display: flex;
	flex-direction: row;
	.MuiTypography-root {
		font-family: Roboto;
		font-size: 0.875rem;
		color: rgba(0, 0, 0, 0.87);
	}
	.MuiRadio-root.Mui-checked {
		color: #6c63ff;
	}
`;

export const RadioStyled = styled(Radio)``;

export const SelectStyled = styled(Select)`
	width: 16.2rem;
`;

export const InputStyled = styled(OutlinedInput)`
	width: 16.2rem;
`;

export const CheckboxStyled = styled(FormControlLabel)`
	padding-left: 1.5rem;
	.MuiTypography-root {
		font-size: 0.75rem;
		font-family: Roboto;
		font-weight: normal;
	}
`;
